import { Component, Fragment } from "react"
import PropTypes from "prop-types"
import { map, omit, keys } from "lodash"
import classNames from "classnames"
import { AppBar, Toolbar, Typography, Drawer, List, ListItem, ListItemButton, ListItemText, ListSubheader, Checkbox, ListItemSecondaryAction, IconButton, Button, Divider } from "@mui/material"
import Tooltip from "components/material/tooltip"
import { ExpandLess, ExpandMore } from "@mui/icons-material"
import ColumnSearch from "../column-search"
import "./styles.scss"

const MAX_COLUMNS = 12

export default class ColumnSelector extends Component {
  static propTypes = {
    open: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    filteredColumnsSelector: PropTypes.array.isRequired,
    selectedColumns: PropTypes.objectOf(PropTypes.bool).isRequired,
    setSelectedColumns: PropTypes.func.isRequired,
    setSort: PropTypes.func.isRequired,
    searchColumn: PropTypes.func.isRequired,
    columnSearches: PropTypes.object.isRequired,
  }

  constructor(...args) {
    super(...args)

    this.state = {
      openSections: {},
    }
  }

  toggleSection = (type, id) => {
    const { searchColumn, columnSearches } = this.props
    const key = `${type}.${id}`

    this.setState(state => {
      let openSections
      if (state.openSections[key]) {
        openSections = omit(state.openSections, key)

        if (columnSearches[key]) {
          searchColumn(key, "")
        }
      } else {
        openSections = { ...state.openSections, [key]: true }
      }

      return { ...state, openSections }
    })
  }

  toggleColumn = (type, id) => {
    const { selectedColumns, setSelectedColumns } = this.props
    const key = `${type}.${id}`

    let newColumns
    if (selectedColumns[key]) {
      newColumns = omit(selectedColumns, key)
      setSelectedColumns(newColumns)
    } else if (keys(selectedColumns).length < MAX_COLUMNS) {
      newColumns = { ...selectedColumns, [key]: true }
      setSelectedColumns(newColumns)
    }
  }

  setOnlyColumn = (type, id) => {
    const { setSelectedColumns } = this.props
    const key = `${type}.${id}`

    setSelectedColumns({ [key]: true })
  }

  setSort = (getter, type, id, direction) => {
    const { setSort, selectedColumns } = this.props

    if (!selectedColumns[`${type}.${id}`]) {
      this.toggleColumn(type, id)
    }

    setSort({
      direction,
      type,
      id,
      getter,
    })
  }

  render() {
    const { openSections } = this.state
    const { open, toggle, selectedColumns, columnSearches, searchColumn, filteredColumnsSelector } = this.props
    const nbSelected = keys(selectedColumns).length

    return (
      <Drawer anchor="right" open={open} onClose={toggle} className="components_column-selector">
        <AppBar position="sticky">
          <Toolbar>
            <Typography
              variant="h6"
              noWrap
              sx={{
              color: "inherit",
            }}
            >
              {nbSelected}/{MAX_COLUMNS} Columns Selected
            </Typography>
          </Toolbar>
        </AppBar>
        {map(filteredColumnsSelector, category =>
          <List key={category.category} subheader={<ListSubheader className="category-header">{category.category}</ListSubheader>}>
            {map(category.columns, column => <Fragment key={`${column.type}-${column.id}`}>
              <ListItemButton
                classes={{ root: column.items ? "list-item group" : classNames("list-item", column.sorted) }}
                onClick={column.items ? () => { this.toggleSection(column.type, column.id) } : () => { this.toggleColumn(column.type, column.id) }}
              >
                <Checkbox
                  color="primary"
                  disabled={!!column.items || (!column.selected && nbSelected >= MAX_COLUMNS)}
                  className="list-checkbox"
                  checked={column.selected}
                  indeterminate={column.partial}
                  tabIndex={-1}
                  disableRipple
                />
                <ListItemText>
                  {column.label}
                </ListItemText>
                {!column.items && <ListItemSecondaryAction className="secondary-actions">
                  <Button onClick={(event) => {
                    event.stopPropagation()
                    this.setOnlyColumn(column.type, column.id)
                  }}
                  >
                    only
                  </Button>
                  <Tooltip title="Sort Asc">
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation()
                        this.setSort(category.sortGetter, column.type, column.id, "ASC")
                      }}
                      size="large"
                    >
                      <i className="fa fa-sort-numeric-asc" style={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Sort Desc">
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation()
                        this.setSort(category.sortGetter, column.type, column.id, "DESC")
                      }}
                      size="large"
                    >
                      <i className="fa fa-sort-numeric-desc" style={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>}
                {column.items && ["desc", "asc"].includes(column.sorted) && <ListItemSecondaryAction className="secondary-actions">
                  {column.sorted === "desc" && <i className="fa fa-sort-numeric-desc" style={{ fontSize: 14 }} />}
                  {column.sorted === "asc" && <i className="fa fa-sort-numeric-asc" style={{ fontSize: 14 }} />}
                </ListItemSecondaryAction>}
                {column.items && (openSections[`${column.type}.${column.id}`] ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>
              {column.items && !!openSections[`${column.type}.${column.id}`] && <>
                <List className="searchable-list" disablePadding subheader={<ColumnSearch value={columnSearches[column.id]} type={column.id} searchColumn={searchColumn} />}>
                  {map(column.items, item =>
                    <ListItem
                      classes={{ container: `list-item ${item.sorted ? item.sorted : ""}` }}
                      className="nested"
                      key={item.id}
                      onClick={() => { this.toggleColumn(column.type, item.id) }}
                    >
                      <Checkbox
                        color="primary"
                        className="list-checkbox"
                        tabIndex={-1}
                        checked={item.selected}
                        disabled={(!item.selected && nbSelected >= MAX_COLUMNS)}
                        disableRipple
                      />
                      <ListItemText>
                        {item.label}
                      </ListItemText>
                      <ListItemSecondaryAction className="secondary-actions">
                        <Button onClick={(event) => {
                          event.stopPropagation()
                          this.setOnlyColumn(column.type, item.id)
                        }}
                        >
                          only
                        </Button>
                        <Tooltip title="Sort Asc">
                          <IconButton
                            onClick={(event) => {
                              event.stopPropagation()
                              this.setSort(category.sortGetter, column.type, item.id, "ASC")
                            }}
                            size="large"
                          >
                            <i className="fa fa-sort-numeric-asc" style={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sort Desc">
                          <IconButton
                            onClick={(event) => {
                              event.stopPropagation()
                              this.setSort(category.sortGetter, column.type, item.id, "DESC")
                            }}
                            size="large"
                          >
                            <i className="fa fa-sort-numeric-desc" style={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>,
                  )}
                </List>
                <Divider className="column-divider" />
              </>}
            </Fragment>)}
          </List>,
        )}
      </Drawer>
    )
  }
}
